import { Types } from 'mongoose';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authenticatedFetch } from '../../authentication/authenticatedFetch';

export enum Role {
  ADMIN = 'admin',
  USER = 'user',
}

interface UserProfile {
  firstName: string;
  lastName: string;
  company: string;
  companyType: string;
  region: string;
  country: string;
  email: string;
  news: boolean;
}

interface UserPassword {
  password?: string;
  formerPassword?: string;
}

export type User = UserProfile &
  UserPassword & { role: Role } & { _id: Types.ObjectId } & {
    seenFeatures: string[];
  } & {
    subscriptionExpirationAt: Date;
    subscriptionStatus?: "processing" | "success" | "failed";
  };

export const isUserSubscribed = (user?: User | null): boolean => {
  if (!user || !user.subscriptionExpirationAt) return false;
  return new Date(user.subscriptionExpirationAt) > new Date();
};

export const getUser = async (): Promise<User> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/authentication/me`,
    {
      method: 'GET',
    },
  );
  return await response.json();
};

const changeProfile = async (user: UserProfile): Promise<User> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/authentication/me`,
    {
      method: 'PUT',
      body: JSON.stringify(user),
    },
  );
  return await response.json();
};

const seenFeature = async (feature: string): Promise<User> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/authentication/me`,
    {
      method: 'PUT',
      body: JSON.stringify({ feature }),
    },
  );
  return await response.json();
};

const changePassword = async (user: UserPassword): Promise<User> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/authentication/me`,
    {
      method: 'PUT',
      body: JSON.stringify(user),
    },
  );
  return await response.json();
};
const useUser = () => {
  const queryClient = useQueryClient();

  const userQuery = useQuery('user', getUser, {
    useErrorBoundary: true,
  });

  const changeProfileMutation = useMutation(
    (user: UserProfile) => changeProfile(user),
    {
      onSuccess: (updatedUser) => {
        queryClient.setQueryData('user', updatedUser);
      },
    },
  );
  const seenFeatureMutation = useMutation(
    (feature: string) => seenFeature(feature),
    {
      onSuccess: (updatedUser) => {
        queryClient.setQueryData('user', updatedUser);
      },
    },
  );
  const changePasswordMutation = useMutation(
    (user: UserPassword) => changePassword(user),
    {
      onSuccess: (updatedUser) => {
        queryClient.setQueryData('user', updatedUser);
      },
    },
  );
  return {
    user: userQuery.data,
    changeProfile: changeProfileMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    seenFeature: seenFeatureMutation.mutateAsync,
    error:
      userQuery.error ||
      changeProfileMutation.error ||
      changePasswordMutation.error ||
      seenFeatureMutation.error,
    loading:
      userQuery.isLoading ||
      changeProfileMutation.isLoading ||
      changePasswordMutation.isLoading ||
      seenFeatureMutation.isLoading,
  };
};

export default useUser;
